/**
 * The one display-timezone rule for the app (ortex-gui#1281).
 *
 * Callers say what a value IS, not which zone to show it in:
 *
 * - "instant":  a point in time (news, alerts, filings, "last updated"). Shown in the user's zone,
 *               setting S1: the device's zone unless they picked one in Settings > Date & time.
 * - "market":   a market-anchored time (intraday index and stock charts, session times). Shown in
 *               the instrument's exchange zone, or in S1 if the user switched S2 to "my time zone".
 * - "trading":  the same choice for the trading pages, setting S3.
 * - "calendar": a calendar date (daily SI, settlement, ex-dividend, expiry). Never shifted: these
 *               arrive as midnight-UTC epochs or "YYYY-MM-DD", so they are read in UTC.
 *
 * This file has no React or Jotai in it, so the unit tests can import it. The Jotai atom and the
 * hooks are in ./state.ts.
 */

export type TimeRole = "instant" | "market" | "trading" | "calendar";

export interface TimeSettings {
    /** S1: "system" (the device's zone, resolved at read time and never stored) or an IANA zone. */
    zone: string;
    /** S2: indexes and market charts. */
    indexes: "exchange" | "user";
    /** S3: trading pages. */
    trading: "exchange" | "user";
}

export const DEFAULT_TIME_SETTINGS: TimeSettings = { zone: "system", indexes: "exchange", trading: "exchange" };

/** The broker's venue zone, used when a trading display has no instrument zone of its own. */
export const BROKER_VENUE_TIMEZONE = "America/New_York";

export const TIME_STORAGE_KEY = "ortex_time";
export const TIME_COOKIE = "ortex_tz";

// Building an Intl.DateTimeFormat is slow, and the zone picker labels ~400 zones on every
// keystroke, so formatters (and validity) are cached per locale and zone.
const labelFormatters = new Map<string, Intl.DateTimeFormat>();
const zoneValidity = new Map<string, boolean>();

const labelFormatter = (locale: "en-US" | "en-GB", zone: string): Intl.DateTimeFormat => {
    const key = `${locale}|${zone}`;
    let f = labelFormatters.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: "short" });
        labelFormatters.set(key, f);
    }
    return f;
};

export const isValidZone = (zone: unknown): zone is string => {
    if (typeof zone !== "string" || !zone) return false;
    let valid = zoneValidity.get(zone);
    if (valid === undefined) {
        try {
            labelFormatter("en-GB", zone);
            valid = true;
        } catch (e) {
            valid = false;
        }
        zoneValidity.set(zone, valid);
    }
    return valid;
};

/** Read the settings-blob value defensively: anything missing or unknown falls back to the default. */
export const normaliseTimeSettings = (value: unknown): TimeSettings => {
    const v = (value ?? {}) as Partial<TimeSettings>;
    return {
        zone: v.zone === "system" || isValidZone(v.zone) ? v.zone : DEFAULT_TIME_SETTINGS.zone,
        indexes: v.indexes === "user" ? "user" : "exchange",
        trading: v.trading === "user" ? "user" : "exchange",
    };
};

export const deviceZone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch (e) {
        return "UTC";
    }
};

/** S1 resolved to an IANA zone. */
export const resolveUserZone = (settings: TimeSettings): string => (settings.zone === "system" || !isValidZone(settings.zone) ? deviceZone() : settings.zone);

/** The zone a value with this role is shown in. `exchangeTz` is the instrument's `exchange_tz`. */
export const zoneFor = (role: TimeRole, settings: TimeSettings, exchangeTz?: string | null): string => {
    if (role === "calendar") return "UTC";
    if (role === "instant") return resolveUserZone(settings);
    const choice = role === "market" ? settings.indexes : settings.trading;
    if (choice === "user") return resolveUserZone(settings);
    const exchange = exchangeTz?.trim();
    if (exchange && isValidZone(exchange)) return exchange;
    return role === "trading" ? BROKER_VENUE_TIMEZONE : resolveUserZone(settings);
};

/** "EDT", "BST", "CEST" for the zone at that instant, from Intl. Falls back to "GMT+9" style. */
export const zoneLabel = (zone: string, at: number | Date = Date.now()): string => {
    try {
        const part = labelFormatter("en-US", zone)
            .formatToParts(new Date(at))
            .find((p) => p.type === "timeZoneName");
        if (!part) return zone;
        // en-US only has abbreviations for American zones; ask en-GB for the European ones.
        if (/^GMT[+-]/.test(part.value)) {
            const gb = labelFormatter("en-GB", zone)
                .formatToParts(new Date(at))
                .find((p) => p.type === "timeZoneName");
            if (gb && !/^GMT[+-]/.test(gb.value)) return gb.value;
        }
        return part.value === "GMT" && zone === "UTC" ? "UTC" : part.value;
    } catch (e) {
        return zone;
    }
};

/** "America/New_York" -> "New York". */
export const zoneCity = (zone: string): string => zone.split("/").pop().replace(/_/g, " ");

// The current settings, for code that formats outside React (dateParser, grid cells, chart
// option builders). The TimeZoneSync component keeps this in step with the Jotai atom. In the
// browser it starts from the localStorage mirror so the first paint already follows the setting.
let current: TimeSettings | null = null;

export const readStoredTimeSettings = (): TimeSettings => {
    try {
        if (typeof window === "undefined") return DEFAULT_TIME_SETTINGS;
        const raw = window.localStorage.getItem(TIME_STORAGE_KEY);
        return raw ? normaliseTimeSettings(JSON.parse(raw)) : DEFAULT_TIME_SETTINGS;
    } catch (e) {
        return DEFAULT_TIME_SETTINGS;
    }
};

export const getTimeSettings = (): TimeSettings => {
    if (!current) current = readStoredTimeSettings();
    return current;
};

/** Called by useTimeSettingsSync while rendering, so formatting outside React follows the atom. */
export const setTimeSettings = (settings: TimeSettings) => {
    current = normaliseTimeSettings(settings);
};

/** Mirror to localStorage (first paint after a reload) and to a cookie the server can read (SSR, later). */
export const storeTimeSettings = (settings: TimeSettings) => {
    const value = normaliseTimeSettings(settings);
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(TIME_STORAGE_KEY, JSON.stringify(value));
    } catch (e) {
        /* storage unavailable */
    }
    try {
        document.cookie = `${TIME_COOKIE}=${encodeURIComponent(resolveUserZone(value))}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (e) {
        /* cookies unavailable */
    }
};

/** The zone for a role under the current settings, for non-React code. */
export const currentZoneFor = (role: TimeRole, exchangeTz?: string | null): string => zoneFor(role, getTimeSettings(), exchangeTz);
